/**
 * Background class-content downloads.
 *
 * The sibling of GlobalUploadContext, and for the same reason: packaging a
 * batch's recordings takes minutes, and holding the user on one screen — or
 * behind a modal spinner — for the whole of it is not a workable way to run a
 * class. A download started here keeps going while the user moves around the
 * app, and reports itself through the floating panel (GlobalDownloadManager),
 * exactly as an upload does.
 *
 * What it does not survive is a page reload or a closed tab: the archive is
 * assembled in this tab's memory, so there is nothing to resume from. The panel
 * says so while a job is running.
 */

import React, {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
} from "react";

import {
    BundleEntry,
    DownloadProgress,
    downloadClassBundle,
} from "@/pages/superadmin-new/components/InstituteManagement_new/InstituteDetail/components/tabs/Academics/components/BatchSection/BatchManagement/utils/classContentDownloader";

export interface DownloadJob {
    id: string;
    /** What the user asked for, e.g. "IMAT 2026 — Physics and Maths". */
    label: string;
    scope: "class" | "batch";
    status: "downloading" | "zipping" | "completed" | "error" | "cancelled";
    /** 0-100, by files finished. Zipping holds at 100 while the archive is built. */
    progress: number;
    completed: number;
    total: number;
    bytes: number;
    /** The file currently being fetched. */
    currentFile: string;
    /** Files that could not be fetched; they are listed inside the zip too. */
    failures: number;
    error?: string;
    /** Set once the archive has been handed to the browser. */
    fileName?: string;
    startedAt: number;
}

interface GlobalDownloadContextType {
    downloadQueue: DownloadJob[];
    /**
     * Queue a bundle and return its job id. Returns immediately — the caller
     * should close whatever panel it was in and let the floating widget report.
     */
    startClassDownload: (args: {
        entries: BundleEntry[];
        zipName: string;
        label: string;
        scope: "class" | "batch";
    }) => string;
    cancelDownload: (id: string) => void;
    removeDownload: (id: string) => void;
}

const GlobalDownloadContext = createContext<GlobalDownloadContextType | undefined>(undefined);

export const useClassDownload = () => {
    const context = useContext(GlobalDownloadContext);
    if (!context) {
        throw new Error("useClassDownload must be used within a GlobalDownloadProvider");
    }
    return context;
};

export const GlobalDownloadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [downloadQueue, setDownloadQueue] = useState<DownloadJob[]>([]);

    // Bundles carry whole ClassConfigs. They are held out of state so a
    // progress tick does not drag them through a re-render.
    const controllers = useRef<Map<string, AbortController>>(new Map());

    const patch = useCallback((id: string, changes: Partial<DownloadJob>) => {
        setDownloadQueue((queue) =>
            queue.map((job) => (job.id === id ? { ...job, ...changes } : job))
        );
    }, []);

    const startClassDownload = useCallback(
        ({ entries, zipName, label, scope }: {
            entries: BundleEntry[];
            zipName: string;
            label: string;
            scope: "class" | "batch";
        }) => {
            const id = `dl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const total = entries.reduce((sum, e) => sum + e.summary.downloadable.length, 0);

            setDownloadQueue((queue) => [
                ...queue,
                {
                    id,
                    label,
                    scope,
                    status: "downloading",
                    progress: 0,
                    completed: 0,
                    total,
                    bytes: 0,
                    currentFile: "",
                    failures: 0,
                    startedAt: Date.now(),
                },
            ]);

            const controller = new AbortController();
            controllers.current.set(id, controller);

            const onProgress = (progress: DownloadProgress) => {
                patch(id, {
                    status: progress.phase === "zipping" ? "zipping" : "downloading",
                    completed: progress.completed,
                    total: progress.total,
                    bytes: progress.bytes,
                    currentFile: progress.currentFile,
                    progress:
                        progress.total > 0
                            ? Math.round((progress.completed / progress.total) * 100)
                            : 0,
                });
            };

            downloadClassBundle(entries, zipName, {
                nestByClass: entries.length > 1,
                signal: controller.signal,
                onProgress,
            })
                .then((outcome) => {
                    patch(id, {
                        status: "completed",
                        progress: 100,
                        currentFile: "",
                        fileName: outcome.fileName,
                        failures: outcome.failures.length,
                        bytes: outcome.bytes,
                    });
                })
                .catch((err: any) => {
                    if (err?.name === "AbortError") {
                        patch(id, { status: "cancelled", currentFile: "" });
                        return;
                    }
                    patch(id, {
                        status: "error",
                        currentFile: "",
                        // Out-of-memory on a large archive surfaces as a generic
                        // allocation failure, so say what usually helps.
                        error: err?.message || "The archive could not be created.",
                    });
                })
                .finally(() => {
                    controllers.current.delete(id);
                });

            return id;
        },
        [patch]
    );

    const cancelDownload = useCallback((id: string) => {
        controllers.current.get(id)?.abort();
        controllers.current.delete(id);
        patch(id, { status: "cancelled", currentFile: "" });
    }, [patch]);

    const removeDownload = useCallback((id: string) => {
        controllers.current.get(id)?.abort();
        controllers.current.delete(id);
        setDownloadQueue((queue) => queue.filter((job) => job.id !== id));
    }, []);

    return (
        <GlobalDownloadContext.Provider
            value={{ downloadQueue, startClassDownload, cancelDownload, removeDownload }}
        >
            {children}
        </GlobalDownloadContext.Provider>
    );
};
